// In-memory auth lock for the browser Supabase client.
//
// WHY: gotrue-js (the auth layer under @supabase/ssr) defaults, in the
// browser, to a cross-tab lock backed by the Web Locks API
// (`navigator.locks`). That lock is held for the duration of each auth
// operation and is shared across every tab AND the installed PWA. If the tab
// holding it is backgrounded, crashes, or its callback never settles, the
// lock is never released — and every other `getUser()` / `getSession()` /
// token refresh that asks for it with an infinite acquire timeout (gotrue's
// default for most internal calls) waits FOREVER. That is the root of the
// "app freezes on the loading screen and never recovers" reports.
//
// This replaces it with a simple in-process promise-chain lock (the same
// strategy as Supabase's built-in `processLock`): calls are serialized within
// a single JS context and never depend on a lock another tab might be holding,
// so an auth call can never hang on a stale lock.
//
// TRADEOFF: serialization is per-tab, not cross-tab, so two tabs could refresh
// a token at the same instant. With cookie-backed session storage that race is
// benign — gotrue recovers from a rotated refresh token, and both tabs
// converge on the latest cookie. A brief, self-healing race is strictly better
// than an unrecoverable freeze.

const chains = new Map<string, Promise<unknown>>()

/**
 * Matches gotrue's lock signature:
 *   `<R>(name, acquireTimeout, fn) => Promise<R>`
 * `acquireTimeout` is intentionally ignored — we never block on an external
 * lock, so there is nothing to time out acquiring.
 */
export function inMemoryAuthLock<R>(
  name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> {
  const prev = chains.get(name) ?? Promise.resolve()
  // Run fn once the previous holder settles, regardless of its outcome, so one
  // failed/rejected operation can never wedge the chain for this name.
  const result = prev.then(
    () => fn(),
    () => fn(),
  )
  // Keep a non-rejecting tail as the chain head so a rejection doesn't block
  // or throw for the next caller.
  chains.set(
    name,
    result.then(
      () => undefined,
      () => undefined,
    ),
  )
  return result
}
